import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';

import {
  ContextualHelpModal,
  ErrorBoundary,
  Logo,
  ActionsPanel,
  AppSwitcher,
  CompanyDropdown,
  SearchInput,
  OptionsDropdown,
  URLS,
  POSITION,
  useAuth0,
  TooltipWrapper,
  useChatbotStatus,
  useTrackInteraction,
  FEATURE_TRACK,
  useGetCompany,
  Popover,
  useToggle,
} from '@livingsecurity/shared';

import { useStepUpAuth } from '../../../hooks/useStepUpAuth';
import { isStepUpAllowed, isSteppedUp } from '../../../utils/authUtils';

import * as Styled from './styles';

/**
 The Navbar component is the top-level component of the application. It contains the logo and actions panel by default and can be extended to include a search input and dashboard items if desired.

 It should always have companyInfo and flags as props. The optional props are for the search input and dashboard items on the participant dashboard.
 */
const NavBar = (props) => {
  const { user, isImpersonate, isLsAdmin, isCompanyAdmin, isReseller } = useAuth0();
  const { data: companyInfo } = useGetCompany();
  const [isStepUpLoading, setIsStepUpLoading] = useState(false);
  const [isExitLoading, setIsExitLoading] = useState(false);

  const {
    dashboardItems: DashboardItems,
    iframeCompanyName,
    withSearch,
    searchPlaceholder,
    height,
    flags,
    isSticky,
    keepBlue,
    chatSlideoutOpen,
    toggleChatSlideout,
  } = props;
  const { t } = useTranslation('playerDashboard');
  const { pathname } = useLocation();
  const { track } = useTrackInteraction();
  const useIframeName = iframeCompanyName && iframeCompanyName?.toLowerCase() !== companyInfo?.name?.toLowerCase();
  const companyName = useMemo(() => {
    if (useIframeName) {
      return iframeCompanyName;
    }
    return companyInfo?.name || iframeCompanyName;
  }, [companyInfo, iframeCompanyName, useIframeName]);
  const [helpModalIsOpen, setHelpModalIsOpen] = useState(false);
  const [helpContext, setHelpContext] = useState({ main: null, secondary: null });
  const [isHelpPopoverOpen, toggleHelpPopover] = useToggle(false);
  const isChatView = pathname.includes(URLS.chatbot);

  const tenantHelpLinks = useMemo(() => {
    const TENANT_HELP_CONFIG = {
      '40d43b09-4867-4417-a959-fb80a2ac7181': [
        { label: 'My Team Report', url: 'https://app.powerbi.com/Redirect?action=OpenReport&appId=f6c52436-6b0b-412b-8dbc-71b84ac63272&reportObjectId=26ae4d8e-4586-4436-aba5-dbd8d08d8d38&ctid=c3549632-51ee-40fe-b6ae-a69f3a6cc157&reportPage=78b63747fe123107ce0e&pbi_source=appShareLink&portalSessionId=4fb07076-726e-4d0c-86dd-8586cd3de053' },
        { label: 'CareerHub Learning', url: 'https://learninghub.ccep.com/pages/27/home' },
        { label: 'Cyber Hub', url: 'https://ccep.sharepoint.com/sites/Comms91_CyberSecurityWeek' },
        { label: 'Living Security Guide', url: 'https://ccep.sharepoint.com/sites/Comms91_CyberSecurityWeek/SitePages/Living%20Security/Living%20Security%20Guide.aspx' },
      ],
      '3b32d93a-a2bb-4ddc-a9d7-f89a666b2460': [
        { label: 'Test Help', url: 'https://learninghub.ccep.com/pages/27/home' },
        { label: 'Test Hub', url: 'https://ccep.sharepoint.com/sites/Comms91_CyberSecurityWeek' },
        { label: 'Living Security Guide', url: 'https://ccep.sharepoint.com/sites/Comms91_CyberSecurityWeek/SitePages/Living%20Security/Living%20Security%20Guide.aspx' },
      ],
    };
    return TENANT_HELP_CONFIG[user.tenant_id] || [];
  }, [user.tenant_id]);

  const { status: chatbotStatus } = useChatbotStatus({
    tenantId: user.tenant_id,
    queryKey: ['chatbotStatus'],
    enabled: flags.allowChatbot,
  });
  const showChatBtn = useMemo(
    () =>
      flags.allowChatbot &&
      !isChatView &&
      ((!isLsAdmin && isCompanyAdmin) || (isLsAdmin && isImpersonate)) &&
      chatbotStatus === 'enabled',
    [isLsAdmin, isCompanyAdmin, isImpersonate, isChatView, flags.allowChatbot, chatbotStatus],
  );

  useEffect(() => {
    if (isChatView && chatSlideoutOpen) {
      sessionStorage?.setItem('UNIFY_ASSIST_OPEN', false);
      toggleChatSlideout();
    }
  }, [isChatView, chatSlideoutOpen, toggleChatSlideout]);

  // Check and reset STEPPED_UP flag on every render
  useEffect(() => {
    if (isStepUpAllowed(user) && isSteppedUp(user) && !localStorage.getItem('STEPPED_UP')) {
      console.log('Resetting STEPPED_UP flag in localStorage to match ID token state');
      localStorage.setItem('STEPPED_UP', 'true');
    }
  });

  const toggleContextualHelp = (main, secondary) => {
    if (main) {
      setHelpContext((prevState) => ({
        ...prevState,
        main,
      }));
    }

    if (secondary) {
      setHelpContext((prevState) => ({
        ...prevState,
        secondary,
      }));
    } else {
      setHelpContext((prevState) => ({
        ...prevState,
        secondary: null,
      }));
    }
    setHelpModalIsOpen(!helpModalIsOpen);
  };

  const isDashboard = window.location.href.indexOf(URLS.dashboard) > -1;

  const trackBtnClick = useCallback(
    (event, metadata = {}) => {
      if (event) track(event, metadata);
    },
    [track],
  );

  const openChatbot = useCallback(() => {
    if (!chatSlideoutOpen) {
      trackBtnClick(FEATURE_TRACK.CHATBOT_VIEW);
      sessionStorage?.setItem('UNIFY_ASSIST_OPEN', true);
    } else {
      sessionStorage?.setItem('UNIFY_ASSIST_OPEN', false);
    }
    toggleChatSlideout();
  }, [chatSlideoutOpen, toggleChatSlideout, trackBtnClick]);

  const { initiateStepUp, exitStepUp, isLoading } = useStepUpAuth();

  const handleStepUpClick = useCallback(async () => {
    if (isStepUpLoading) return;
    setIsStepUpLoading(true);
    try {
      await initiateStepUp();
    } catch (error) {
      console.error('Failed to initiate step-up authentication:', error);
      setIsStepUpLoading(false);
    }
  }, [initiateStepUp, isStepUpLoading]);

  const handleExitClick = useCallback(() => {
    if (isExitLoading) return;
    setIsExitLoading(true);
    exitStepUp();
  }, [exitStepUp, isExitLoading]);

  const ProductName = () => {
    let productName = 'TRAINING';
    if (isDashboard || pathname.includes(URLS.dashboard) || pathname.includes(URLS.player)) {
      return null;
    }
    if (pathname.includes(URLS.teamsBase)) {
      productName = 'CYBERESCAPE ONLINE';
    } else if (pathname.includes(URLS.unify)) {
      productName = 'UNIFY';
    } else if (pathname.includes(URLS.phishing)) {
      productName = 'PHISH';
    } else if (pathname.includes(URLS.support)) {
      productName = 'SUPPORT';
    } else if (pathname.includes(URLS.companies) || pathname.includes(URLS.userManagement)) {
      productName = 'ADMIN';
    } else if (pathname.includes(URLS.chatbot)) {
      productName = 'CHAT';
    }
    return (
      <Styled.ProductName data-testid="navbar-product-name" aria-label="product name">
        {productName}
      </Styled.ProductName>
    );
  };

  return (
    <>
      {helpModalIsOpen && (
        <ContextualHelpModal
          onClose={() => setHelpModalIsOpen(false)}
          mainContent={helpContext.main}
          title={t('navbar-items.badges.contextual-help.title')}
        />
      )}
      <Styled.Wrapper
        keepBlue={keepBlue}
        dashboardView={isDashboard}
        data-testid="header"
        aria-label="header"
        barHeight={height}
        isSticky={isSticky}
      >
        <Styled.LeftContainer>
          {!isDashboard && (isLsAdmin || isCompanyAdmin || isReseller) && (
            <AppSwitcher trackBtnClick={trackBtnClick} chatEnabled={chatbotStatus === 'enabled'} {...props} />
          )}
          <Logo tenantId={user.tenant_id} reload={companyInfo?.id !== user?.tenant_id} />
          {tenantHelpLinks?.length > 0 && isDashboard && (
            <Styled.HelpIconWrapper>
              <Popover
                isOpen={isHelpPopoverOpen}
                position={POSITION.BOTTOM}
                onClickOutside={toggleHelpPopover}
                content={
                  <Styled.HelpPopoverContent>
                    <h4>Quick Links</h4>
                    <ul>
                      {tenantHelpLinks.map((link) => (
                        <li key={link.url}>
                          <a href={link.url} target="_blank" rel="noopener noreferrer">
                            {link.label}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </Styled.HelpPopoverContent>
                }
              >
                <Styled.HelpLinkButton
                  onClick={toggleHelpPopover}
                  aria-label="Quick Links"
                  data-testid="tenant-help-button"
                >
                  Quick Links
                </Styled.HelpLinkButton>
              </Popover>
            </Styled.HelpIconWrapper>
          )}
          <ProductName />
        </Styled.LeftContainer>
        <ErrorBoundary scope="Navbar">
          {!flags.hideParticipantDashboardSearch && withSearch && (
            <SearchInput
              placeholder={searchPlaceholder}
              onChange={() => console.log('Search needs setting up')}
              width="544px"
              height="48px"
            />
          )}
          <Styled.ItemsWrapper dashboardView={isDashboard} barHeight={height}>
            {DashboardItems && <DashboardItems flags={flags} toggleContextualHelp={toggleContextualHelp} />}

            <>
              {/* Admin button for step-up authentication */}
              {isStepUpAllowed(user) && !isSteppedUp(user) && (
                <Styled.AdminButton
                  onClick={handleStepUpClick}
                  className="admin-button"
                  aria-label="Admin access"
                  disabled={isStepUpLoading || isLoading}
                >
                  <i className="fa-duotone fa-shield" />
                  <span>{(isStepUpLoading || isLoading) ? 'Loading...' : 'LSAdmin'}</span>
                </Styled.AdminButton>
              )}

              {/* Exit button for when user is stepped up */}
              {isStepUpAllowed(user) && isSteppedUp(user) && (
                <Styled.AdminButton
                  onClick={handleExitClick}
                  className="admin-button"
                  aria-label="Exit admin access"
                  disabled={isExitLoading || isLoading}
                >
                  <i className="fa-duotone fa-right-from-bracket" />
                  <span>{(isExitLoading || isLoading) ? 'Loading...' : 'Exit'}</span>
                </Styled.AdminButton>
              )}

              {isDashboard && flags.tmpHideLegacyParticipantDashboard ? (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    height: '57px',
                    position: 'relative',
                    zIndex: 4,
                    color: 'white',
                  }}
                >
                  <OptionsDropdown id="options-menu" user={user} {...props} />
                </div>
              ) : (
                <Styled.RightContainer>
                  {companyName && (isCompanyAdmin || (isLsAdmin && isImpersonate)) && (
                    <TooltipWrapper
                      position={POSITION.RIGHT}
                      content={companyName}
                      showTooltip={useIframeName || companyName?.length > 25}
                    >
                      <CompanyDropdown
                        companyInfo={companyInfo}
                        companyName={companyName}
                        useIframeName={useIframeName}
                      />
                    </TooltipWrapper>
                  )}
                  {showChatBtn && (
                    <Styled.ChatbotButton onClick={openChatbot}>
                      <i className="fa-duotone fa-messages" />
                    </Styled.ChatbotButton>
                  )}
                  <ActionsPanel {...props} />
                </Styled.RightContainer>
              )}
            </>
          </Styled.ItemsWrapper>
        </ErrorBoundary>
      </Styled.Wrapper>
    </>
  );
};

NavBar.propTypes = {
  /**
   * Gamification dashboard items to be rendered in the navbar
   * Level, Badges, Rank, and Total Points depending on feature flags status
   */
  dashboardItems: PropTypes.elementType,

  /**
   * Whether to show the assignment search input
   *
   * @default false
   */
  withSearch: PropTypes.bool,

  /**
   * Placeholder text for the assignment search input
   *
   * @default "Search Assignments"
   */
  searchPlaceholder: PropTypes.string,

  /**
   * Height of the navbar in pixels
   *
   * This is used to set the height of the navbar in the legacy view
   *
   * @default ""
   */
  height: PropTypes.string,

  toggleChatSlideout: PropTypes.func,

  /**
   * Feature flags passed from launch darkly service
   * @see https://launchdarkly.com/
   *
   */
  flags: PropTypes.shape({
    hideParticipantDashboardSearch: PropTypes.bool,
    tmpHideLegacyParticipantDashboard: PropTypes.bool,
  }).isRequired,
};

NavBar.defaultProps = {
  dashboardItems: null,
  withSearch: false,
  searchPlaceholder: 'Search Assignments',
  toggleChatSlideout: () => {},
  height: '',
};

export default NavBar;
